import { useEffect, useState } from "react";
import api from "../lib/api";
import { Panel, Stat } from "../components/common";
import { corrColor, fmt, chgColor, classLabel } from "../lib/format";
import { LineChart, Line, ResponsiveContainer, YAxis, Tooltip } from "recharts";
import { Grid3x3, Waypoints, Radar, GitBranch } from "lucide-react";

const WINDOWS = [[20, "20D"], [60, "60D"], [120, "120D"], [252, "1Y"], [756, "3Y"], [1260, "5Y"]];

const RISK_META = {
  "Correlation Breakdown": { color: "#EF4444", note: "Stocks & bonds falling together — diversification is failing (inflationary shock)." },
  "Risk-Off Contagion": { color: "#F97316", note: "Equities moving as one bloc — systemic de-risking underway." },
  "Dispersion — Stock-Picker's Market": { color: "#10B981", note: "Low cross-asset correlation — selection & relative value dominate." },
  "Normal Diversification": { color: "#00E5FF", note: "Healthy cross-asset dispersion — hedges behaving as expected." },
};

function Sparkline({ series, color }) {
  return (
    <ResponsiveContainer width="100%" height={44}>
      <LineChart data={series} margin={{ top: 4, right: 2, left: 2, bottom: 0 }}>
        <YAxis domain={[-1, 1]} hide />
        <Tooltip
          contentStyle={{ background: "#0E131F", border: "1px solid #334155", borderRadius: 6, fontSize: 11, fontFamily: "JetBrains Mono" }}
          labelFormatter={(l) => l} formatter={(v) => [v, "corr"]} />
        <Line type="monotone" dataKey="v" stroke={color} strokeWidth={1.6} dot={false} isAnimationActive={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

function PairCard({ p }) {
  const rc = { aligned: "#10B981", inverse: "#EF4444", decoupled: "#F59E0B" }[p.regime];
  return (
    <div className="panel-2 rounded-md p-3" data-testid={`intermarket-pair-${p.a}-${p.b}`} style={{ borderColor: `${rc}33` }}>
      <div className="flex items-center justify-between">
        <div>
          <div className="font-mono font-bold text-sm">{p.pair}</div>
          <div className="eyebrow">{p.label}</div>
        </div>
        <div className="text-right">
          <div className="font-mono font-bold text-lg tnum" style={{ color: rc }}>{fmt(p.current, 2)}</div>
          <div className="text-[10px] font-mono" style={{ color: chgColor(p.change) }}>Δ {fmt(p.change, 2)}</div>
        </div>
      </div>
      <Sparkline series={p.series} color={rc} />
      <div className="flex items-center justify-between text-[10px] font-mono text-slate-500 mt-1">
        <span>avg {fmt(p.avg, 2)}</span>
        <span className="uppercase tracking-wider" style={{ color: rc }}>{p.regime}</span>
        <span>rng {fmt(p.min, 2)}/{fmt(p.max, 2)}</span>
      </div>
    </div>
  );
}

export default function Correlations() {
  const [corr, setCorr] = useState(null);
  const [inter, setInter] = useState(null);
  const [win, setWin] = useState(60);

  useEffect(() => {
    api.get("/correlations", { params: { window: win } }).then((r) => setCorr(r.data)).catch(() => {});
  }, [win]);

  useEffect(() => {
    api.get("/intermarket").then((r) => setInter(r.data)).catch(() => {});
  }, []);

  const rm = inter ? (RISK_META[inter.risk_state] || RISK_META["Normal Diversification"]) : null;

  return (
    <div className="space-y-5" data-testid="correlations-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">intermarket engine</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Intermarket & Correlation Regime</h1>
        </div>
        <div className="flex gap-1.5">
          {WINDOWS.map(([w, l]) => (
            <button key={w} data-testid={`corr-window-${w}`} onClick={() => setWin(w)}
              className={`text-xs font-mono px-3 py-1.5 rounded-md border transition-colors ${win === w ? "bg-[#00E5FF12] text-[#00E5FF] border-[#00E5FF44]" : "text-slate-400 border-[#334155] hover:bg-[#141C2E]"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* --- correlation regime banner --- */}
      {inter && (
        <div className="panel p-4 flex flex-col md:flex-row md:items-center gap-4" data-testid="correlation-regime-banner"
          style={{ borderColor: `${rm.color}44` }}>
          <div className="flex items-center gap-3 md:w-96">
            <Waypoints size={22} style={{ color: rm.color }} />
            <div>
              <div className="eyebrow">correlation regime</div>
              <div className="font-display text-xl font-extrabold" style={{ color: rm.color }}>{inter.risk_state}</div>
            </div>
          </div>
          <div className="text-xs font-mono text-slate-400 flex-1">{rm.note}</div>
          <div className="flex gap-6">
            <Stat label="Avg Equity Corr" value={fmt(inter.avg_equity_corr, 2)} color={rm.color} />
            <Stat label="Stock/Bond Corr" value={fmt(inter.stock_bond_corr, 2)} color={chgColor(-inter.stock_bond_corr)} />
            <Stat label="Concentration" value={fmt(inter.concentration_index, 1)} color={rm.color} />
          </div>
        </div>
      )}

      {/* --- key intermarket relationships --- */}
      {inter && (
        <Panel title="Key Intermarket Relationships — rolling 60D correlation" icon={<GitBranch size={13} />}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="intermarket-pairs-grid">
            {inter.pairs.filter((p) => p.group !== "forex").map((p) => <PairCard key={p.pair} p={p} />)}
          </div>
        </Panel>
      )}

      {/* --- forex vs indices correlations --- */}
      {inter && inter.pairs.some((p) => p.group === "forex") && (
        <Panel title="Forex ↔ Indices — rolling 60D correlation" icon={<GitBranch size={13} />}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="forex-pairs-grid">
            {inter.pairs.filter((p) => p.group === "forex").map((p) => <PairCard key={p.pair} p={p} />)}
          </div>
        </Panel>
      )}

      {/* --- asset-class cluster matrix --- */}
      {inter && (
        <Panel title="Asset-Class Cluster Correlation (60D)" icon={<Radar size={13} />}>
          <div className="overflow-x-auto">
            <table className="border-collapse" data-testid="cluster-correlation-grid">
              <tbody>
                <tr>
                  <td className="sticky left-0 bg-[#0E131F]"></td>
                  {inter.cluster_classes.map((c) => (
                    <td key={c} className="text-[10px] font-mono text-slate-400 px-2 text-center min-w-[70px]">{classLabel[c] || c}</td>
                  ))}
                </tr>
                {inter.clusters.map((row) => (
                  <tr key={row.cls}>
                    <td className="text-[10px] font-mono text-slate-400 pr-2 text-right sticky left-0 bg-[#0E131F] font-bold whitespace-nowrap">{classLabel[row.cls] || row.cls}</td>
                    {inter.cluster_classes.map((c) => {
                      const v = row.values[c];
                      return (
                        <td key={c} className="min-w-[70px] h-9 text-center align-middle border border-[#0A0E16]"
                          style={{ background: row.cls === c ? "#334155" : corrColor(v) }}>
                          <span className="text-[10px] font-mono tnum text-slate-100">{v != null ? Number(v).toFixed(2) : ""}</span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {/* --- full asset correlation matrix --- */}
      <Panel title={`Pearson Correlation — rolling ${WINDOWS.find(([w]) => w === win)?.[1] || win}`} icon={<Grid3x3 size={13} />}>
        {!corr ? <div className="font-mono text-sm text-slate-500 animate-pulse">computing…</div> : (
          <div className="overflow-x-auto">
            <table className="border-collapse" data-testid="correlation-matrix-grid">
              <tbody>
                <tr>
                  <td className="sticky left-0 bg-[#0E131F]"></td>
                  {corr.symbols.map((s) => <td key={s} className="text-[10px] font-mono text-slate-400 px-1 text-center min-w-[34px]">{s}</td>)}
                </tr>
                {corr.symbols.map((a) => (
                  <tr key={a}>
                    <td className="text-[10px] font-mono text-slate-400 pr-2 text-right sticky left-0 bg-[#0E131F] font-bold">{a}</td>
                    {corr.symbols.map((b) => {
                      const v = corr.matrix[a][b];
                      const tr = corr.trend[a][b];
                      return (
                        <td key={b} title={`${a}/${b}: ${v} (Δ ${tr})`}
                          className="min-w-[34px] h-8 text-center align-middle border border-[#0A0E16]"
                          style={{ background: a === b ? "#334155" : corrColor(v) }}>
                          <span className="text-[9px] font-mono tnum text-slate-100">{v != null ? v.toFixed(2) : ""}</span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-4 flex items-center gap-4 text-[11px] font-mono text-slate-400">
          <span className="flex items-center gap-1"><span className="h-3 w-3 rounded" style={{ background: corrColor(-0.9) }} />-1.0 inverse</span>
          <span className="flex items-center gap-1"><span className="h-3 w-3 rounded" style={{ background: corrColor(0) }} />0 uncorrelated</span>
          <span className="flex items-center gap-1"><span className="h-3 w-3 rounded" style={{ background: corrColor(0.9) }} />+1.0 aligned</span>
          <span className="ml-auto">hover a cell for correlation trend Δ vs prior window</span>
        </div>
      </Panel>
    </div>
  );
}
